import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, ViewChild, effect, inject } from '@angular/core';
import { EFLogicService } from './environmental-fate.logic.service';
import { EFTopologyComponent } from '../ef-topology/ef-topology/ef-topology.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectedProjectApiService } from 'src/app/shared/components/selected-project/selected-project.api.service';
import { Message } from 'primeng/api';
import { Project } from 'src/app/shared/models/project';
import { CustomTabMenuItem } from 'src/app/shared/models/custom-tab-menu-item';
import { Constants } from 'src/app/shared/utils/constants';
import { Environmental_Assessments, UserLogicService } from 'src/app/shared/services/user.logic.service';
import { ModelLibrary, ModelLibraryCollection } from 'src/app/shared/models/model-library';
import { firstValueFrom, take } from 'rxjs';
import { ModelLibraryApiService } from 'src/app/shared/services/model-library.api.service';
import { OrderByPipe } from 'src/app/shared/pipes/order-by.pipe';
import { EFSetupColDef } from '../ef-setup/ef-setup/ef-setup-coldef';
import { DisciplineApiService } from 'src/app/shared/services/echo/discipline.api.service';
import { Discipline } from 'src/app/shared/models/echo/discipline';
import { SetupLogicService } from 'src/app/shared/services/setup.logic.service';

@Component({
  selector: 'app-environmental-fate',
  templateUrl: './environmental-fate.component.html',
  styleUrls: ['./environmental-fate.component.css']
})
export class EnvironmentalFateComponent implements OnInit, OnDestroy {
  activeItem?: CustomTabMenuItem;
  message!: Message;
  loading: boolean = false;
  nextIndex?: number;
  selectedProject?: Project;
  selectedProjectStatus?: number;
  isSetupValid: boolean = false;
  destroyRef = inject(DestroyRef);
  isProjectOwnershipValid: boolean = true;
  environmentalAssessment: Environmental_Assessments = Environmental_Assessments.EF;
  columnDef: any[] = [];
  disciplinePk: number = 0;
  modelsCollection: ModelLibraryCollection[] = [];
  cakeModels: ModelLibrary[] = [];
  featureAcronym = Environmental_Assessments.EF;

  @ViewChild(EFTopologyComponent) efTopologyComponent?: EFTopologyComponent;

  private setupLogicService = inject(SetupLogicService);
  private efSetupColDef = inject(EFSetupColDef);

  constructor(
    public EFLogicService: EFLogicService,
    private selectedProjectApiService: SelectedProjectApiService,
    private userLogicService: UserLogicService,
    private orderByPipe: OrderByPipe,
    private modelLibraryApiService: ModelLibraryApiService,
    private disciplineApiService: DisciplineApiService,
    private cdr: ChangeDetectorRef
  ) {}

  selectedGeographyEffect = effect(() => {
    if (this.setupLogicService.selectedGeographyKey()) {
      this.initColumnDef();
    }
  });

  async ngOnInit(): Promise<void> {
    this.loading = true;
    await this.getDiscipline();
    await this.initModels();
    this.initColumnDef();
    this.initSubscribes();
    const firstItem = this.EFLogicService.items.find((i: CustomTabMenuItem) => i.index === 0);
    if (firstItem) {
      this.EFLogicService.setActiveItem(firstItem);
    }
  }

  async getDiscipline(): Promise<void> {
    await firstValueFrom(this.disciplineApiService.getDisciplineByName(Constants.DISCIPLINE_IDENTITIES.ENVIRONMENTAL_E_FATE).pipe(take(1))).then((discipline: Discipline) => {
      this.disciplinePk = discipline?.disciplinePk ?? -1;
    });
  }

  async initModels() {
    await firstValueFrom(this.modelLibraryApiService.getModels()).then((library: ModelLibrary[]) => {
      const models = library.filter(f => 
        f.disciplineXModel?.some(d => d == this.disciplinePk) &&
        f.nameVersion?.toLowerCase().includes('cake')
      );
      this.cakeModels = this.orderByPipe.transform(models, 'nameVersion');
      
      this.modelsCollection = [{ name: 'cake', library: this.cakeModels }];
    });
  }

  initColumnDef() {
    const selectedGeography = this.setupLogicService.selectedGeographyKey();
    const geographyFilteredCakeModels = selectedGeography
      ? this.cakeModels.filter(model =>
          model.geographyXModel?.some(geography => this.setupLogicService.isSelectedGeography(geography, selectedGeography))
        )
      : this.cakeModels;

    this.columnDef = this.efSetupColDef.getColumnsDefinition(geographyFilteredCakeModels, this.isProjectOwnershipValid);
  }

  onActiveIndexChange(index: number) {
    if (this.activeItem?.index === 1 && index !== 1) {
      this.efTopologyComponent?.logTopologySavePayload();
    }

    this.nextIndex = index;
    this.EFLogicService.setActiveIndexChanged(true, index);
  }

  initSubscribes(): void {
    this.EFLogicService.activeItem.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeItem: CustomTabMenuItem) => {
      this.activeItem = activeItem;
    });

    this.selectedProjectApiService.selectedProject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project?: Project) => {
      this.selectedProject = project;
      this.isProjectOwnershipValid = this.selectedProject ? this.userLogicService.verifyProjectOwnership(this.selectedProject?.createdBy) : true;
      if (this.selectedProject) {
        const firstItem = this.EFLogicService.items.find((i: CustomTabMenuItem) => i.index === 0);
        if (firstItem) {
          this.EFLogicService.setActiveItem(firstItem);
        }
      }
      this.onActiveIndexChange(0);
    });

    this.selectedProjectApiService.selectedProjectStatus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status?: number) => {
      this.selectedProjectStatus = status;
    });

    this.EFLogicService.isLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isLoading: boolean) => {
      this.loading = isLoading;
    });

    this.EFLogicService.success.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((success: boolean) => {
      this.loading = false;
      if (success) {
        const nextItem = this.EFLogicService.items.find((i: CustomTabMenuItem) => i.index == this.nextIndex);
        if (nextItem) {
          this.EFLogicService.setActiveItem(nextItem);
        }
      }
    });
  }

  validateSetup(event: boolean) {
    this.isSetupValid = event;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.selectedGeographyEffect.destroy();
  }
}
